import React, { useEffect, useRef } from "react";
import { Redirect, Route, matchPath, useLocation } from "react-router-dom";
import { setAuthorization } from "../helpers/api_helper";
import { useDispatch } from "react-redux";

import { useProfile } from "../Components/Hooks/UserHooks";
import { isTokenExpired } from "../helpers/authToken";
import { rememberPreviousPath } from "../helpers/functions/listOrigin";
import { logoutUser } from "../store/actions";
import { savePostLoginRedirect } from "../helpers/postLoginRedirect";

// Task 24831 - one-shot recovery stamp, so the reload below can never loop.
const PUBLIC_RECOVERY_STAMP = "public_route_recovery_at";
const PUBLIC_RECOVERY_WINDOW_MS = 20000;

const recoveredRecently = () => {
  try {
    const at = parseInt(sessionStorage.getItem(PUBLIC_RECOVERY_STAMP) || "0", 10);
    return !!at && Date.now() - at < PUBLIC_RECOVERY_WINDOW_MS;
  } catch (_) {
    // Storage blocked: assume we already tried rather than risk a reload loop.
    return true;
  }
};

const AuthProtected = ({ children, publicPaths }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { userProfile, loading, token } = useProfile();

  // Task 24831 - THIS GUARD MUST NEVER TOUCH A PUBLIC PAGE.
  //
  // A Scoozi guest registering for a pre-order was thrown to /login. Proof was
  // in localStorage: post_login_redirect held their /manage-event/<token> URL,
  // and that key is written in exactly one place - the branch below. So the
  // guest's PUBLIC page was rendered inside this guard, which has no session to
  // find because a guest never has one.
  //
  // /manage-event/:token is in publicRoutes and loads perfectly on a direct
  // hit; it only fails on the client-side history.push straight after
  // registering. So the outer Switch in Routes/index.js picks the public branch
  // on a fresh load and the authenticated branch on that one push. The root
  // cause of that mis-match is still open (Task 24831).
  //
  // What is NOT conditional on finding it is this: a guard for authenticated
  // routes has no business logging anyone out of a page that was never its to
  // protect. The dispatch(logoutUser()) below is what made the failure stick -
  // it clears the session and routes to /login, so a single bad render became a
  // dead end rather than a flicker.
  const isPublicPath =
    !!publicPaths &&
    !!matchPath(location.pathname, { path: publicPaths, exact: false });

  // Defence-in-depth: useProfile already reports an expired token as null, but
  // guard here too so a stale token can never render protected children.
  const hasValidSession = !!token && !isTokenExpired(token);

  useEffect(() => {
    // Task 24831 - a public URL that reached this guard is a routing fault, not
    // a failed login. Leave the session alone and let the recovery below run.
    if (isPublicPath) return;

    // If authenticated, ensure API client has the token
    if (hasValidSession && userProfile && !loading) {
      setAuthorization(token);
      return;
    }

    // If unauthenticated, save attempted path and logout (clears auth & routes to /login)
    if (!hasValidSession) {
      try {
        const attempted = `${location.pathname}${location.search || ""}${
          location.hash || ""
        }`;
        // Change #100336 - saved with whose session it was, so a different
        // user signing in next is not sent here instead of their landing page.
        savePostLoginRedirect(attempted);
      } catch (_) {
        // ignore storage errors
      }
      dispatch(logoutUser());
    }
  }, [
    isPublicPath,
    hasValidSession,
    token,
    userProfile,
    loading,
    location.pathname,
    location.search,
    location.hash,
    dispatch,
  ]);

  // Task 24831 - recover the page rather than just declining to break it.
  //
  // A full load of the same URL is known to work - that is exactly how the
  // guest links behave when opened from an email, and how I reproduced the
  // working case. So re-enter the app at this URL and the public branch renders
  // it. Stamped in sessionStorage so this can happen at most once per 20s: if
  // reloading does not fix it the page must not sit in a refresh loop, which
  // would be a worse failure than the one being fixed.
  useEffect(() => {
    if (!isPublicPath) return;
    if (recoveredRecently()) return;
    try {
      sessionStorage.setItem(PUBLIC_RECOVERY_STAMP, String(Date.now()));
    } catch (_) {
      return; // cannot stamp, so cannot bound the reload - do not reload
    }
    window.location.replace(
      `${location.pathname}${location.search || ""}${location.hash || ""}`
    );
  }, [isPublicPath, location.pathname, location.search, location.hash]);

  // Task 24795 - remember the route we were on BEFORE this one, so a detail
  // page can tell whether the list that registered itself is genuinely where it
  // was opened from, rather than one visited three navigations ago.
  //
  // The PREVIOUS path, not the current one, and kept in a ref rather than read
  // back out of storage: every page is lazy(), so on a cold chunk the router
  // renders a Suspense fallback and this effect fires before the real page
  // renders. Storing "the current path" would mean a detail page always found
  // its own URL waiting for it. Written once per navigation, it then holds
  // still no matter when the page gets round to asking.
  //
  // Detail pages rewrite their own URL for tab changes with raw
  // window.history.pushState, which react-router never sees - so this does not
  // fire for a tab change, and the origin survives moving between tabs.
  const currentPathRef = useRef(null);
  useEffect(() => {
    if (!hasValidSession) return;
    const path = `${location.pathname}${location.search || ""}`;
    if (currentPathRef.current && currentPathRef.current !== path) {
      rememberPreviousPath(currentPathRef.current);
    }
    currentPathRef.current = path;
  }, [hasValidSession, location.pathname, location.search]);

  // Task 24831 - never send a public page to /login. The reload above is the
  // way out; until it happens, render nothing rather than the authenticated
  // route tree, whose catch-all would show the 404 page over a valid URL.
  if (isPublicPath) {
    return null;
  }

  // Block access to protected children when not authenticated (or expired)
  if (!hasValidSession) {
    return <Redirect to={{ pathname: "/login", state: { from: location } }} />;
  }

  return <>{children}</>;
};

const AccessRoute = ({ component: Component, ...rest }) => (
  <Route
    {...rest}
    render={(props) => (
      <>
        <Component {...props} />
      </>
    )}
  />
);

export { AuthProtected, AccessRoute };
